import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SeoProps {
  title?: string;
  description?: string;
  path?: string; // path starting with '/'
  image?: string;
  locale?: 'zh-CN' | 'en-US';
  alternates?: Array<{ hrefLang: string; href: string }>;
  noindex?: boolean;
  jsonLd?: Record<string, unknown> | Array<Record<string, unknown>>;
}

const SITE_URL = (import.meta.env.VITE_SITE_URL as string) || 'https://example.com';

export const Seo: React.FC<SeoProps> = ({
  title,
  description,
  path,
  image,
  locale = 'zh-CN',
  alternates = [],
  noindex = false,
  jsonLd,
}) => {
  const url = path ? `${SITE_URL}${path}` : SITE_URL;
  const metaTitle = title ? `${title} | LuxEthics` : 'LuxEthics';
  const metaDesc = description?.slice(0, 160);

  const altSet = new Map<string, string>();
  for (const a of alternates) {
    if (a && a.hrefLang && a.href) altSet.set(a.hrefLang, a.href);
  }
  // 自动追加 x-default 到 canonical
  if (!altSet.has('x-default')) {
    altSet.set('x-default', url);
  }

  return (
    <Helmet>
      <html lang={locale} />
      <title>{metaTitle}</title>
      {metaDesc && <meta name="description" content={metaDesc} />}
      <meta property="og:type" content="website" />
      <meta property="og:title" content={metaTitle} />
      {metaDesc && <meta property="og:description" content={metaDesc} />}
      <meta property="og:url" content={url} />
      {image && <meta property="og:image" content={image} />}
      <link rel="canonical" href={url} />
      {Array.from(altSet.entries()).map(([hrefLang, href]) => (
        <link key={hrefLang} rel="alternate" hrefLang={hrefLang} href={href} />
      ))}
      {noindex && <meta name="robots" content="noindex,nofollow" />}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={metaTitle} />
      {metaDesc && <meta name="twitter:description" content={metaDesc} />} 
      {image && <meta name="twitter:image" content={image} />}
      {jsonLd && (
        Array.isArray(jsonLd) ? (
          jsonLd.map((obj, idx) => (
            <script key={idx} type="application/ld+json">{JSON.stringify(obj)}</script>
          ))
        ) : (
          <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
        )
      )}
    </Helmet>
  );
};

export default Seo;
